/**
 * Per-client product configuration — the versioned source of truth for the
 * choices that differ per client and have NO other home. See ProjectTasks #115.
 *
 * Deliberately NARROW. Three things are already configured elsewhere and are NOT
 * duplicated here:
 *   - branding      → `config/appIdentity.ts` (VITE_APP_* env per build)
 *   - module set    → `config/tesseractConfig.ts` (VITE_MODULES / isModuleEnabled)
 *                     + the Active-Organization layer (#116) for per-org scope
 *   - data / auth   → the client's own Supabase clone (clone-per-customer, #67)
 *
 * What lives here: per-client FEATURE FLAGS — product decisions like "does the
 * worker's timesheet show an estimated-pay summary?" that today are hardcoded
 * global consts and therefore flip-flop between people (SHOW_HOURLY_PAY_SUMMARY
 * went Connor→off / Josh→on the same day). Making them a reviewed, per-client
 * config kills that tug-of-war: the demo/internal default now keeps worker pay
 * hidden (Connor's standing decision); a client who wants worker-visible pay
 * flips it on for their clone, without anyone editing a component.
 *
 * Selection: `VITE_CLIENT` picks the config for a clone build; unset → 'internal'
 * (the demo/dev build), so nothing changes today. The P2 "Preview as <client>"
 * control will overlay a runtime selection on top of this (see #115).
 */

import { getPreviewClientKey } from './previewClient'

/** Per-client feature toggles. Add a flag here, default it in INTERNAL, then
 *  read it via `clientFeature('x')` at the call-site. */
export interface ClientFeatureFlags {
  /** Show estimated pay (the weekly gross-pay summary card + the inline per-day
   *  "$ … est." on the hourly worker timesheet). Default OFF — workers do not see
   *  gross-pay estimates on their own timesheet (Connor, 2026-07-08). A client who
   *  explicitly wants worker-visible pay flips it on. */
  showHourlyPaySummary: boolean
}

export interface ClientProductConfig {
  /** Stable key; matches VITE_CLIENT for a clone build. */
  key: string
  /** Human label for the (P2) "Preview as <client>" picker. */
  label: string
  features: ClientFeatureFlags
}

/** INTERNAL = the demo/dev build baseline a new client config overrides
 *  field-by-field. Features default on EXCEPT worker-visible pay estimates, which
 *  are off by decision (workers don't see gross pay on their own timesheet). */
const INTERNAL: ClientProductConfig = {
  key: 'internal',
  label: 'Internal (all features)',
  features: {
    showHourlyPaySummary: false,
  },
}

/**
 * Client registry. Each entry overrides INTERNAL's features for that client.
 * Spread INTERNAL.features first so a new flag added above auto-defaults to the
 * internal value for every client until they explicitly opt out.
 */
const CLIENT_PRODUCTS: Record<string, ClientProductConfig> = {
  internal: INTERNAL,
  // Stub — fill from the Codan engagement (Meetings/Customer Meetings/Codan).
  // Example: Codan's worker timesheet should NOT surface estimated pay.
  codan: {
    key: 'codan',
    label: 'Codan',
    features: { ...INTERNAL.features, showHourlyPaySummary: false },
  },
}

const ACTIVE_CLIENT_KEY: string =
  (import.meta.env as Record<string, string | undefined>).VITE_CLIENT?.trim() || 'internal'

/**
 * The active client's product config. A live "Preview as <client>" selection
 * (#115 P2) takes precedence; otherwise resolves from VITE_CLIENT for a clone
 * build, else INTERNAL. Every feature read routes through here, so the preview
 * overlay is this one spot.
 */
export function activeClientConfig(): ClientProductConfig {
  const previewKey = getPreviewClientKey()
  const key = previewKey ?? ACTIVE_CLIENT_KEY
  return CLIENT_PRODUCTS[key] ?? INTERNAL
}

/** Read one feature flag for the active client. */
export function clientFeature<K extends keyof ClientFeatureFlags>(key: K): ClientFeatureFlags[K] {
  return activeClientConfig().features[key]
}

/** All registered client configs — for the (P2) preview picker. */
export function listClientProducts(): ClientProductConfig[] {
  return Object.values(CLIENT_PRODUCTS)
}
